import { createContext, useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Client from '../api/client';

// Create the context
const ConfigContext = createContext();

// Custom hook to use the config context
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

// Provider component
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    open_bookmarks_in_new_window: false,
    isLoading: true,
  });

  const client = useMemo(() => new Client(), []);

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      if (client.loggedIn()) {
        try {
          const configData = await client.fetchConfiguration();
          if (configData) {
            setConfig({
              ...configData,
              isLoading: false,
            });
          } else {
            setConfig(prevConfig => ({
              ...prevConfig,
              isLoading: false,
            }));
          }
        } catch (error) {
          console.error('Error loading configuration:', error);
          setConfig(prevConfig => ({
            ...prevConfig,
            isLoading: false,
          }));
        }
      } else {
        // Not logged in, use default values
        setConfig({
          open_bookmarks_in_new_window: false,
          isLoading: false,
        });
      }
    };

    loadConfig();
  }, [client]);

  // Update configuration
  const updateConfig = async newConfig => {
    // If it's just a state update (not an API call)
    if (
      typeof newConfig === 'object' &&
      !Object.prototype.hasOwnProperty.call(newConfig, 'isLoading')
    ) {
      setConfig(prevConfig => ({
        ...prevConfig,
        ...newConfig,
        isLoading: false,
      }));
      return newConfig;
    }

    // If we need to update the server (requires login)
    if (!client.loggedIn()) {
      return;
    }

    try {
      setConfig(prevConfig => ({
        ...prevConfig,
        ...newConfig,
        isLoading: true,
      }));

      const updatedConfig = await client.updateConfiguration(newConfig);

      setConfig({
        ...updatedConfig,
        isLoading: false,
      });

      return updatedConfig;
    } catch (error) {
      console.error('Error updating configuration:', error);

      // Revert to previous state on error
      setConfig(prevConfig => ({
        ...prevConfig,
        isLoading: false,
      }));

      throw error;
    }
  };

  // Toggle open_bookmarks_in_new_window setting
  const toggleOpenInNewWindow = async () => {
    try {
      await updateConfig({
        open_bookmarks_in_new_window: !config.open_bookmarks_in_new_window,
      });
    } catch (error) {
      console.error(
        'Error toggling open_bookmarks_in_new_window setting:',
        error
      );
    }
  };

  const value = {
    config,
    updateConfig,
    toggleOpenInNewWindow,
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

ConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ConfigContext;
